<script setup>
import {GenderLabel, IsChildLabel, YesNoLabels} from "@/choices";
import {convertPriorityToColor, HpoTransportStatus, PriorityLabel} from "@/components/hpo/choices";
import WizardSummary from "@/components/wizard/WizardSummary.vue";
import WizardSummaryField from "@/components/wizard/WizardSummaryField.vue";
import {unitType} from "@/components/hpo/utils";
import {computed} from "vue";
import {HospitalTypeLabel} from "../choices";

const props = defineProps({
    isEditing: {
        type: Boolean,
        default: false,
    },
    patientId: {
        type: [Number, String],
    },
    formData: {
        type: Object,
        required: true,
    },
    existingTransportStatus: {
        type: Number,
        default: null,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    }
});
const emit = defineEmits(['pushStep', 'submit']);

const isNewOrDraft = computed(() => {
    return props.existingTransportStatus === null || props.existingTransportStatus === HpoTransportStatus.DRAFT;
});

const transportTerminatable = computed(() => {
    return [
        HpoTransportStatus.DISP_PENDING,
        HpoTransportStatus.HOSPITAL_PENDING,
        HpoTransportStatus.READY,
        HpoTransportStatus.ON_WAY,
    ].includes(props.existingTransportStatus);
});

const editDisabled = computed(() => {
    return transportTerminatable.value;
});

function goToStep(stepName) {
    emit('pushStep', stepName);
}

function submit() {
    emit('submit', {status: HpoTransportStatus.DISP_PENDING, data: props.formData});
}

function saveDraft() {
    emit('submit', {status: HpoTransportStatus.DRAFT, data: props.formData});
}

function terminateTransport() {
    emit('submit', {status: HpoTransportStatus.TERMINATED, data: props.formData});
}
</script>

<template>
    <main>
        <h1>{{ pgettext('title', 'Summary') }}</h1>

        <v-alert v-if="patientId && isEditing" type="info" variant="tonal" class="mb-8">
            <strong>
                {{
                    interpolate(gettext('You are editing patient %(tic)s.'), {tic: `${formData.ticPrefix}${formData.ticNumber}`})
                }}
            </strong>
        </v-alert>

        <div class="d-flex justify-center">
            <WizardSummary vertical>
                <WizardSummaryField :title="pgettext('label', 'TIC')" @edit="goToStep('tic')" :disableEdit="isEditing || editDisabled">
                    {{ formData.ticPrefix }}{{ formData.ticNumber }}
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Gender')" @edit="goToStep('gender')" :disableEdit="editDisabled">
                    {{ GenderLabel[formData.gender] }}
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Age')" @edit="goToStep('isChild')" :disableEdit="editDisabled">
                    {{ IsChildLabel[formData.isChild] }}
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Transport Priority')" @edit="goToStep('transportPriority')" :disableEdit="editDisabled">
                    <v-chip v-if="formData.transportPriority" density="compact" variant="flat" size="large" :ripple="false"
                            :color="convertPriorityToColor(formData.transportPriority).toLowerCase()" rounded class="py-0 px-5">
                        <strong>{{ PriorityLabel[formData.transportPriority] }}</strong>
                    </v-chip>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Diagnose')" small @edit="goToStep('diagnose')" :disableEdit="editDisabled">
                    {{ formData.diagnose }}
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Ventilated')" @edit="goToStep('ventilated')" :disableEdit="editDisabled">
                    {{ YesNoLabels[formData.ventilated] }}
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Transport Unit')" @edit="goToStep('unit')" :disableEdit="editDisabled">
                    {{ unitType(formData.unitType, formData.unitTypeSpec) }} {{ formData.callSign }}
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Hospital Type')" @edit="goToStep('hospitalType')" :disableEdit="editDisabled">
                    {{ HospitalTypeLabel[formData.hospitalType] }}
                </WizardSummaryField>
            </WizardSummary>
        </div>

        <div class="text-center mt-8">
            <v-btn v-if="transportTerminatable" @click="terminateTransport()" color="red" size="large" :loading="isSubmitting">
                {{ pgettext('action', 'Cancel Transport') }}
            </v-btn>
            <div v-else class="d-flex ga-6 flex-column align-center">
                <v-btn @click="submit()" color="green" size="large" :loading="isSubmitting">
                    {{ pgettext('action', 'Save') }}
                </v-btn>

                <v-btn v-if="isNewOrDraft" @click="saveDraft()" color="grey" :loading="isSubmitting">
                    {{ pgettext('action', 'Save Draft') }}
                </v-btn>
            </div>
        </div>
    </main>
</template>
